import {
  $mealSwaps,
  $subscriptionWeeks,
} from '@/stores/meal-program-subscription/stores'
import type {
  AvailableMeals,
  MealProperty,
  Meal as PopsMeal,
} from '@/types/pops'
import dayjs from '@/utilities/dayjs-config'

// Used for iterations. Defines the order in which meals should appear.
export const MEAL_TYPES: AvailableMeals[] = ['breakfast', 'lunch', 'dinner']

const MEAL_LETTER_TO_MEAL_TYPE_MAP = {
  B: 'breakfast',
  L: 'lunch',
  D: 'dinner',
}

const MEAL_TYPE_TO_MEAL_LETTER_MAP = {
  breakfast: 'B',
  lunch: 'L',
  dinner: 'D',
}

type MealLetterCode = 'B' | 'L' | 'D'

export class Meal {
  #rawMeal: PopsMeal
  id: number
  date: dayjs.Dayjs
  mealType: AvailableMeals
  week: dayjs.Dayjs
  title: string
  description: string
  ingredients: string
  properties: MealProperty[]
  mealImageUrl: string
  mealIds: number[]
  new: boolean
  thumbnailUrl: string
  dayImageUrl: string
  mealSwap?: number
  #isHeatable?: boolean
  #isSpicy?: boolean
  #hasNuts?: boolean
  #hasSoy?: boolean
  #defaultMeal?: Meal
  #isChefInResidence?: boolean
  #mealCode?: string
  #mealLetterCode?: MealLetterCode
  #mealDateCode?: any

  constructor(rawMeal: PopsMeal) {
    this.id = rawMeal.id
    this.#rawMeal = rawMeal
    this.date = dayjs(rawMeal.date)
    this.mealType = rawMeal.meal
    this.week = dayjs(rawMeal.week)
    this.title = rawMeal.title
    this.description = rawMeal.description
    this.ingredients = rawMeal.ingredients
    this.properties = rawMeal.properties
    this.mealImageUrl = rawMeal.meal_image_url
    this.thumbnailUrl = rawMeal.thumbnail_url
    this.dayImageUrl = rawMeal.day_image_url
    this.mealSwap = rawMeal.meal_swap
    this.mealIds = [3235] //Chef in Residence kms_meal_ids
    this.new = rawMeal.new
  }

  get defaultMeal(): Meal | undefined {
    if (this.#defaultMeal) return this.#defaultMeal
    if (!this.mealSwap) return undefined

    const mealSwaps = $mealSwaps.get()[this.week.format('YYYY-MM-DD')]

    if (!mealSwaps) return undefined

    const mealSwap = mealSwaps.find((mealSwap) => mealSwap.id === this.mealSwap)

    if (!mealSwap) return undefined

    const { swapFromMealCode } = mealSwap

    const subscriptionWeek =
      $subscriptionWeeks.get()[this.week.format('YYYY-MM-DD')]

    this.#defaultMeal =
      subscriptionWeek.availableMenu.getMealByMealCode(swapFromMealCode)

    return this.#defaultMeal
  }

  get mealCode() {
    if (this.#mealCode) return this.#mealCode

    this.#mealCode = `${this.mealLetterCode}${this.mealDateCode}`

    return this.#mealCode
  }

  get mealLetterCode() {
    if (this.#mealLetterCode) return this.#mealLetterCode

    this.#mealLetterCode = MEAL_TYPE_TO_MEAL_LETTER_MAP[
      this.mealType
    ] as MealLetterCode

    return this.#mealLetterCode
  }

  get mealDateCode(): number {
    if (this.#mealDateCode) return this.#mealDateCode

    this.#mealDateCode = this.date.day()

    return this.#mealDateCode
  }

  // TODO: refactor
  get isHeatable() {
    if (this.#isHeatable) return this.#isHeatable

    this.#isHeatable = this.properties.includes('heatable')

    return this.#isHeatable
  }

  get isSpicy() {
    if (this.#isSpicy) return this.#isSpicy

    this.#isSpicy = this.properties.includes('spicy')

    return this.#isSpicy
  }

  get hasNuts() {
    if (this.#hasNuts) return this.#hasNuts

    this.#hasNuts = this.properties.includes('nuts')

    return this.#hasNuts
  }

  get hasSoy() {
    if (this.#hasSoy) return this.#hasSoy

    this.#hasSoy = this.properties.includes('soy')

    return this.#hasSoy
  }

  get isChefInResidence() {
    if (this.#isChefInResidence) return this.#isChefInResidence

    let matchingId = this.mealIds.find((id) => id === this.#rawMeal.kms_meal_id)

    if (matchingId) {
      return (this.#isChefInResidence = true)
    } else {
      return (this.#isChefInResidence = false)
    }
  }

  isSwappableWith(meal: Meal) {
    if (!meal || this.isSame(meal)) return false

    if (meal.mealType === 'breakfast') {
      return this.mealType === 'breakfast'
    }

    return this.mealType === 'lunch' || this.mealType === 'dinner'
  }

  isSame(meal: Meal) {
    return this.mealCode === meal.mealCode
  }

  static getMealTypeFromMealCode(mealCode: string) {
    return MEAL_LETTER_TO_MEAL_TYPE_MAP[mealCode[0] as MealLetterCode]
  }

  /**
   * Gets the date from the given meal code where 0 is Sunday.
   *
   * @example
   * getDateFromMealCode('D0', dayjs('2021-03-17')) => dayjs('2021-03-21')
   */
  static getDateFromMealCode(mealCode: string, weekContext = dayjs()) {
    const date = dayjs(weekContext)
    let day = parseInt(mealCode[1])

    // We need to use '7' instead of '0' for Sundays because weeks run from
    // Monday to Sunday and '0' would give us the Sunday from the previous week.
    day = day === 0 ? 7 : day

    return date.isoWeekday(day)
  }
}
